/* ============
 * Dashboard
 * ============
 *
 * This is the dashboard page.
 */

import store from './../../store';
import accountService from './../../services/account';
import authService from './../../services/auth';
import orderProductService from './../../services/orderProduct';
import orderService from './../../services/order';
import ediOrderService from './../../services/ediOrder';
import shippingService from './../../services/shippingProvider';
import { mapGetters, mapMutations } from 'vuex';
import shippingTransformer from './../../transformers/shippingConfirm';
import Forms from './../../utils/forms/forms';
import warehouseService from './../../services/warehouse';
import organizationSettingsService from './../../services/organizationSettings';
import moment from 'moment';
import QuickProductEditExcise from './../../components/quickProductEditExcise/quickProductEditExcise.vue';

export default {
  data() {
    return {
      form: new Forms({
        confirmDate: {
          value: moment().format('Y-MM-DD HH:mm:ss'),
          type: 'date',
        },
        deliveryDate: {
          value: '',
          type: 'date',
        },
        warehouseId: {
          type: 'select',
          value: '',
        },
      }),
      loading: false,
    };
  },

  computed: {
    ...mapGetters({
      currentOrder: 'currentOrder',
      orderProducts: 'allOrderProducts',
      warehouses: 'allWarehouses',
      account: 'account',
      currentEdiOrder: 'currentEdiOrder',
      settings: 'organizationSettings',
    }),
    isZNB() {
      return this.currentEdiOrder && this.currentEdiOrder.ediOrderTypeName === 'ZNB';
    },
    bevearageProducts() {
      return this.orderProducts.filter(p => p.productType === 'Beverage' || p.productTypeGroup === 'Beverage').map((item) => {
        const excise = [];
        if (item.exciseAlcoholGroup) {
          excise.push(item.exciseAlcoholGroupName);
        }
        if (item.excisePackagingGroup) {
          excise.push(item.excisePackagingGroupName);
        }
        if (item.exciseBasicGroup) {
          excise.push(item.exciseBasicGroupName);
        }
        return {
          id: item.id,
          name: item.name,
          beverage: item.exciseAlcoholGroupName || '-',
          packaging: item.excisePackagingGroupName || '-',
          basic: item.exciseBasicGroupName || '-',
          excisable: item.excisable,
          bottleVolume: item.fieldsJSON.bottle_volume,
          alcoholPercentage: item.fieldsJSON.alcohol_percentage,
          productPackaging: item.productPackagingId || '',
          noExcise: !item.excisable || excise.length === 0 || !item.fieldsJSON.bottle_volume || !item.fieldsJSON.alcohol_percentage,
        };
      });
    },
    hasNoExcise() {
      return this.bevearageProducts.filter(p => p.noExcise).length !== 0 && this.hasExciseModule;
    },
    hasMissingPackaging() {
      let hasInventory = false;
      if (this.account.modules.length > 0) {
        for (let i = 0; i < this.account.modules.length; i++) {
          if (this.account.modules[i].name == 'Inventory') {
            hasInventory = true;
            return this.bevearageProducts.length && this.bevearageProducts.filter(p => !p.productPackaging).length !== 0;
          }
        }
      }
      return hasInventory;
    },
    hasExciseModule() {
      let hasExciseModule = false;
      if (this.account.modules.length > 0) {
        for (let i = 0; i < this.account.modules.length; i++) {
          if (this.account.modules[i].name == 'Excise') {
            hasExciseModule = true;
          }
        }
      }
      return hasExciseModule;
    },
  },

  mounted() {
    this.getResources();
  },

  watch: {
    /**
     * Watches state update to inject on Forms class.
     *
     * @param  {Object} shipping providers    The shipping providers list.
     */
    shippingProviders(shippingProviders) {
      const hasModule = false;
      this.form.setOptions('shippingProviderId', shippingProviders.map(c => ({ id: c.id, name: `${c.name} ` })));
    },
    warehouses(warehouses) {
      let hasModule = false;
      if (this.account.modules.length > 0) {
        for (let i = 0; i < this.account.modules.length; i++) {
          if (this.account.modules[i].name == 'Inventory') {
            hasModule = true;
            this.form.setOptions('warehouseId', warehouses.items);
          }
        }
      }

      if (!hasModule) {
        this.form.hideField('warehouseId');
      }
    },
  },

  methods: {
    ...mapMutations({
      toggle: 'TOGGLE_MODAL',
      setModalContent: 'SET_MODAL_CONTENT',
      setModalProps: 'SET_MODAL_PROPS',
      unsetModalContent: 'UNSET_MODAL_CONTENT',
      setModalCloseOnClick: 'SET_MODAL_CLOSE_ON_CLICK',
      setModalShowClose: 'SET_MODAL_SHOW_CLOSE',
      setModalCloseOnPressEscape: 'SET_MODAL_CLOSE_ON_PRESS_ESCAPE',
    }),

    quickProductEdit(id) {
      const item = this.orderProducts.find(p => +p.id === +id);
      if (item) {
        this.setModalProps({
          productId: item.productId,
          productName: item.name,
          orderProduct: item,
          isEdi: true,
        });
        this.toggle(true);
        this.setModalContent(() => QuickProductEditExcise);
      }
    },

    /**
     * Fetch the component's required resources.
     */
    getResources() {
      this.loading = true;
      Promise.all([
        shippingService.all(),
        warehouseService.all(),
        orderProductService.all(this.currentOrder.id),
        organizationSettingsService.show(),
      ]).then(() => {
        this.loading = false;
        this.form.assignData({ deliveryDate: moment(this.currentEdiOrder.requestedDeliveryDate.split('.').join('-'), 'DD-M-Y').format('Y-MM-DD') });
        this.form.warehouseId = this.currentOrder.warehouseId || this.settings.defaultWarehouse;
      });
    },
    store() {
      this.loading = false;
      this.form.loading = true;
      const client = shippingTransformer.send(
        Object.assign({}, this.form.data())
      );

      return ediOrderService.confirm(this.currentEdiOrder.id, client)
        .then((data) => {
          this.loading = false;
          if (data.xml) {
            this.$msgbox({
              title: 'XML',
              message: data.xml,
              customClass: 'xmlModalCustomClass',
            });
          } else {
            this.$message({
              showClose: true,
              message: this.$t('static.ediOrders.edit.confirmSuccess'),
              customClass: 'success',
            });
          }
          orderService.show(this.currentOrder.id);
          this.closeModal();
        }).catch((errors) => {
          this.loading = false;
          this.form.loading = false;
          let message = errors.data.message || '';
          if (errors.data.confirm_date) {
            message = errors.data.confirm_date[0];
          } else if (errors.data.delivery_date) {
            message = errors.data.delivery_date[0];
          }

          this.$message({
            showClose: true,
            message,
            customClass: 'error',
          });
        });
    },
    nextProcess() {
      this.setModalContent(() => require('./../../components/batchAssignPopup/batchAssignPopup.vue'));
      this.setModalProps({
        order: this.currentOrder,
        orderProduct: this.orderProducts,
      });
      this.setModalShowClose(true);
      this.setModalCloseOnPressEscape(true);
    },
    /*
     * Closes the modal
     */
    closeModal() {
      this.toggle(false);
      this.unsetModalContent();
    },
  },
  components: {
    VForm: require('./../../components/form/form.vue'),
    VMessage: require('./../../components/form/message/message.vue'),
  },
};



// WEBPACK FOOTER //
// ./src/app/components/ediOrderConfirm/ediOrderConfirm.js